import Offcanvas from "react-bootstrap/Offcanvas";
import Dropdown from "react-bootstrap/Dropdown";
import React, { useMemo } from "react";

import { useTranslation } from "react-i18next";
import {
  PlusLg,
  Person,
  QuestionCircle,
  Globe,
  Star,
  LayoutTextWindowReverse,
  Share,
  CreditCard2Back,
  FileEarmark,
  Folder,
  ChevronDown,
  BoxArrowRight,
  BarChart,
  Plus,
} from "react-bootstrap-icons";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import useGetUrlLocale from "../../hooks/useGetUrlLocale";
import { Col, Spinner } from "react-bootstrap";

import UserData from "./userData";
import Projects from "./projects";
import { useUserQuery } from "../../graphql/menu.hooks";

import {
  usePagesByDomainQuery,
  usePagesByFolderQuery,
} from "../../graphql/projects.hooks";
import { DomainFolderProps } from "../../pages/projects/projectPage/folderPage";
import { useIsNoShared } from "../../hooks/useIsNoShared";
import { useReactiveVar } from "@apollo/client";
import {
  isMenuShow,
  isShowNewProjectModal,
  isPremiumOnlyRestricted,
  isTrialEndedShow,
} from "../../graphql/state";
import { isRestrictedAllowedRoute } from "../../services/subscriptionTrial";
import { useExitRegimeImpersonateMutation } from "../../graphql/userAdministration.hooks";

const languages = [
  { value: "en", label: "English" },
  { value: "es", label: "Español" },
  { value: "pt", label: "Português" },
  { value: "ru", label: "Русский" },
  { value: "ms", label: "Bahasa Melayu" },
  { value: "vi", label: "Tiếng Việt" },
  { value: "fr", label: "Français" },
  { value: "de", label: "Deutsch" },
  { value: "ar", label: "عرب" },
  { value: "id", label: "bahasa Indonesia" },
  { value: "th", label: "ไทย" },
  { value: "ko", label: "한국인" },
  { value: "ja", label: "日本" },
  { value: "nl", label: "Nederlands" },
  { value: "it", label: "Italiano" },
  { value: "sv", label: "Svenska" },
  { value: "uk", label: "Українська" },
  { value: "zh", label: "中国人" },
  { value: "he", label: "עִברִית" },
  { value: "hi", label: "हिंदी" },
];

const ME_PAGE_LINK = process.env.REACT_APP_ME_PAGE_URL;

export default function Menu() {
  const navigate = useNavigate();
  const lng = useGetUrlLocale();
  const { projectId, id, item } = useParams();
  const { data: user } = useUserQuery();
  const location = useLocation();
  const isNoShared = useIsNoShared();
  const isShow = useReactiveVar(isMenuShow);
  const premiumOnlyRestricted = useReactiveVar(isPremiumOnlyRestricted);
  const currentUser = user?.user.email;
  const isProjects = /\/projects($|\/)/.test(location.pathname);
  const { data: pagesByFolderData } = usePagesByFolderQuery({
    variables: {
      folderId: Number(id),
    },
    skip: !id || Boolean(item),
  });
  const [exitRegimeImpersonate, { loading: loadingExit }] =
    useExitRegimeImpersonateMutation();

  const folderAccessType: DomainFolderProps | undefined = useMemo(() => {
    return (
      pagesByFolderData?.pagesByFolder?.folder?.accessFolderDomainByPriority ||
      undefined
    );
  }, [pagesByFolderData]);

  const { data: pagesByDomainData } = usePagesByDomainQuery({
    variables: {
      domainId: Number(projectId),
    },
    skip: !projectId || Boolean(item),
  });

  const projectAccessType = useMemo(() => {
    if (!folderAccessType) {
      return pagesByDomainData?.pagesByDomain?.domain?.userAccesses?.find(
        (user) => user?.user?.email === currentUser
      )?.accessType;
    }
    return undefined;
  }, [pagesByDomainData, currentUser, folderAccessType]);

  const handleCloseOffcanvas = () => isMenuShow(false);

  const handleRestrictedNavigation = (event: React.MouseEvent<HTMLElement>) => {
    if (!premiumOnlyRestricted) return;

    const link = (event.target as HTMLElement).closest("a");
    if (link) {
      const url = new URL(link.href, window.location.origin);
      const isExternal = url.origin !== window.location.origin;
      if (isExternal || isRestrictedAllowedRoute(url.pathname)) return;
    }

    event.preventDefault();
    event.stopPropagation();
    isTrialEndedShow(true);
  };

  const noShare = user?.user?.domains?.find(
    (domain) => domain.id === Number(projectId)
  );

  const { t, i18n } = useTranslation();
  const lang = i18n.language.split("-")[0];

  const createFolderLink = id
    ? `${lng}/folder/new?parentFolder=${id}`
    : `${lng}/folder/new?parentDomain=${projectId}`;

  const changeLanguage = (lng: string) => {
    const queryParams = new URLSearchParams(location.search).toString();

    const currentPath = location.pathname
      .split("/")
      .slice(i18n.language.split("-")[0] === "en" ? 1 : 2)
      .join("/");

    const newPath = lng === "en" ? `/${currentPath}` : `/${lng}/${currentPath}`;
    const finalPath = queryParams ? `${newPath}?${queryParams}` : newPath;

    navigate(finalPath, { replace: true });
    i18n.changeLanguage(lng);
  };
  const handleExit = () => {
    window.location.href = `${process.env.REACT_APP_ME_PAGE_URL}/logout`;
  };

  const handleExitImpersonate = () => {
    exitRegimeImpersonate({
      onCompleted: () => {
        setTimeout(() => {
          window.location.href = `${window.location.origin}${lng}/user-administration`;
        }, 1000);
      },
    });
  };

  return (
    <>
      <Col
        xxl={2}
        lg={3}
        md={4}
        xs={12}
        className="d-lg-flex flex-column justify-content-between h-100 custom-scrollbar pb-4 fixed-top d-none overflow-y-auto"
      >
        <div onClickCapture={handleRestrictedNavigation}>
          <a
            href={`${process.env.REACT_APP_ME_PAGE_URL}${lng}`}
            className="text-center mt-4 mb-5 d-lg-block "
          >
            <img src={`${ME_PAGE_LINK}/images/logo.svg`} alt="Logo" />
          </a>
          <p className="text-dark px-4 fs-7 d-none d-lg-block">
            {t("menu.main")}
          </p>
          <ul className="nav left-nav-pills nav-block flex-column mb-sm-auto mb-0 align-items-center align-items-sm-start px-lg-4 w-100 fs-7">
            <li className="nav-item w-100 mb-2">
              <Dropdown>
                <Dropdown.Toggle
                  variant="primary"
                  id="dropdown-basic"
                  className="text-white py-2 fs-7 w-100 fw-normal d-flex justify-content-between align-items-center"
                >
                  <div>
                    <PlusLg className="me-2" />
                    <span>{t("menu.create")}</span>
                  </div>
                  <ChevronDown />
                </Dropdown.Toggle>
                <Dropdown.Menu className="w-100 fs-7">
                  {user?.user?.domains.length > 0 && (
                    <Dropdown.Item
                      as={Link}
                      to={{
                        pathname: `${lng}/page/create`,
                        search: `?type=1${id ? `&parentFolder=${id}` : ""}${projectId ? `&projectId=${projectId}` : isProjects ? `&projectId=${user?.user?.domains[0]?.id}` : ""}${!isNoShared && !isProjects ? `&Shared` : ""}`,
                      }}
                      className="py-2 px-3 mb-1"
                      onClick={() => {
                        window.gtag?.("event", "create_page");
                      }}
                    >
                      <FileEarmark className="me-2" />
                      {t("menu.page")}
                    </Dropdown.Item>
                  )}
                  {projectId &&
                  (!projectAccessType || projectAccessType === 2) &&
                  (!folderAccessType || folderAccessType === 2) ? (
                    <>
                      <Dropdown.Item
                        as={Link}
                        className=" py-2 px-3 mb-1"
                        to={createFolderLink}
                        onClick={() => {
                          window.gtag?.("event", "create_folder");
                        }}
                      >
                        <Folder className="me-2" />
                        {t("menu.folder")}
                      </Dropdown.Item>
                    </>
                  ) : null}
                  <Dropdown.Item
                    className="py-2 px-3 mb-1"
                    onClick={() => {
                      isShowNewProjectModal(true);
                      window.gtag?.("event", "create_project");
                    }}
                  >
                    <Star className="me-2" />
                    {t("menu.project")}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </li>
            {user?.user.admin && (
              <>
                <li className="nav-item w-100 mb-2">
                  <Link
                    to={`${lng}/page/all`}
                    className={`nav-link ${location.pathname.includes("/page/all") ? "active" : ""}`}
                    onClick={handleCloseOffcanvas}
                  >
                    <Plus className="me-2 fs-4" />
                    <span>All Pages</span>
                  </Link>
                </li>
                <li className="nav-item w-100 mb-2">
                  <Link
                    to={`${lng}/folder/all`}
                    className={`nav-link ${location.pathname.includes("/folder/all") ? "active" : ""}`}
                    onClick={handleCloseOffcanvas}
                  >
                    <Plus className="me-2 fs-4" />
                    <span>All Folders</span>
                  </Link>
                </li>
              </>
            )}
            <li className="nav-item w-100 mb-2">
              <Link
                to={`${lng}/projects`}
                className={`nav-link ${location.pathname === `/projects` || (noShare && location.pathname.includes("/projects")) ? "active" : ""}`}
              >
                <Star className="me-2" />
                <span>{t("menu.myProjects")}</span>
              </Link>
            </li>
            <Projects handleCloseOffcanvas={handleCloseOffcanvas} />
          </ul>

          <ul className="nav left-nav-pills nav-block flex-column mb-sm-auto mb-0 align-items-center align-items-sm-start px-lg-4 w-100 fs-7">
            <li className="nav-item w-100 mb-2 ps-3">
              <Link
                to={`${lng}/shared`}
                className={`nav-link ${
                  location.pathname.includes("/shared") ||
                  (!noShare && location.pathname.includes("/projects/"))
                    ? "active"
                    : ""
                }`}
              >
                <Share className="me-2" />
                <span>{t("menu.shared")}</span>
              </Link>
            </li>
            <li className="nav-item w-100 mb-2">
              <Link
                to={`${lng}/profile`}
                className={`nav-link ${location.pathname.includes("/profile") ? "active" : ""}`}
              >
                <Person className="me-2" />
                <span>{t("menu.profile")}</span>
              </Link>
            </li>
            <li className="nav-item w-100 mb-2">
              <Link to={`${lng}/statistic-dashboard`} className="nav-link">
                <BarChart className="me-2" />
                <span>{t("menu.statistics")}</span>
              </Link>
            </li>
            <li className="nav-item w-100 mb-2">
              <Link to={`${lng}/payment`} className="nav-link">
                <CreditCard2Back className="me-2" />
                <span>{t("menu.paymentSubscription")}</span>
              </Link>
            </li>
            <li className="nav-item w-100 mb-2">
              <Link
                to={`${lng}/support`}
                className={`nav-link ${location.pathname.includes("/support") ? "active" : ""}`}
              >
                <QuestionCircle className="me-2" />
                <span>{t("menu.support")}</span>
              </Link>
            </li>
            {user?.user.admin && (
              <li className="nav-item w-100 mb-2">
                <Link
                  to="/template"
                  className={`nav-link ${location.pathname === "/template" ? "active" : ""}`}
                >
                  <LayoutTextWindowReverse className="me-2" />
                  <span>{t("menu.templates")}</span>
                </Link>
              </li>
            )}
            {user?.user.admin && (
              <li className="nav-item w-100 mb-2">
                <Link
                  to="/page/category"
                  className={`nav-link ${location.pathname.includes("/page/category") ? "active" : ""}`}
                >
                  <QuestionCircle className="me-2" />
                  <span>Page Categories</span>
                </Link>
              </li>
            )}
            {user?.user.admin && (
              <li className="nav-item w-100 mb-2">
                <Link
                  to="/template/category"
                  className={`nav-link ${location.pathname.includes("/template/category") ? "active" : ""}`}
                >
                  <QuestionCircle className="me-2" />
                  <span>Template Categories</span>
                </Link>
              </li>
            )}
            {user?.user.admin && (
              <li className="nav-item w-100 mb-2">
                <Link
                  to="/subpage-templates"
                  className={`nav-link ${location.pathname.includes("/subpage-templates") ? "active" : ""}`}
                >
                  <QuestionCircle className="me-2" />
                  <span>Subpage Templates</span>
                </Link>
              </li>
            )}
            {user?.user.admin && (
              <li className="nav-item w-100 mb-2">
                <Link
                  to={`${process.env.REACT_APP_ME_PAGE_URL}/blog-admin/blog/article`}
                  className={`nav-link`}
                  onClick={handleCloseOffcanvas}
                  target="_blank"
                >
                  <QuestionCircle className="me-2" />
                  <span>Blog Articles</span>
                </Link>
              </li>
            )}
            {user?.user.admin && (
              <>
                <li className="nav-item w-100 mb-2">
                  <Link
                    to={`${lng}/user-administration`}
                    className={`nav-link ${location.pathname.includes("/user-administration") ? "active" : ""}`}
                  >
                    <QuestionCircle className="me-2" />
                    <span>Users</span>
                  </Link>
                </li>
                <li className="nav-item w-100 mb-2">
                  <Link
                    to={`${lng}/page-blocker`}
                    className={`nav-link ${location.pathname.includes("/page-blocker") ? "active" : ""}`}
                  >
                    <QuestionCircle className="me-2" />
                    <span>Page Blocker</span>
                  </Link>
                </li>
              </>
            )}
          </ul>
        </div>
        <div className=" px-4">
          <UserData />
          <div className="ps-3 d-flex justify-content-between pe-5">
            <div className="">
              {user &&
                (user?.user?.originalUserIfSwitched ? (
                  <div
                    className="nav-link cursor-pointer"
                    onClick={handleExitImpersonate}
                  >
                    <BoxArrowRight className="me-3" />
                    <span className="fs-7">Exit</span>{" "}
                    {loadingExit && <Spinner size="sm" />}
                  </div>
                ) : (
                  <a href="#" className="nav-link " onClick={handleExit}>
                    <BoxArrowRight className="me-3" />
                    <span className=" fs-7">{t("menu.logout")}</span>
                  </a>
                ))}
            </div>
            <Dropdown>
              <Dropdown.Toggle
                id="dropdown-basic"
                variant="link"
                className="nav-link dropdown-toggle text-uppercase text-gray"
              >
                <Globe className="me-3" />
                <small>{lang}</small>
                <ChevronDown className="ms-2" />
              </Dropdown.Toggle>

              <Dropdown.Menu>
                {languages.map((item) => (
                  <Dropdown.Item
                    key={item.value}
                    onClick={() => changeLanguage(item.value)}
                    className={`${lng.slice(1) === item.value && "text-primary"} ${item.value === "en" && lng === "" && "text-primary"}`}
                  >
                    {item.label}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </Col>
      <nav className="navbar bg-white fixed-top d-lg-none">
        <div className="mobile-navbar-container container-fluid d-flex align-items-center justify-content-center position-relative">
          <button
            onClick={() => isMenuShow(true)}
            className="navbar-toggler border-0 position-absolute"
            type="button"
            data-bs-toggle="offcanvas"
            data-bs-target="#offcanvasNavbar"
            aria-controls="offcanvasNavbar"
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          <a className="navbar-brand m-0" href={ME_PAGE_LINK + lng}>
            <img
              width={91}
              height={30}
              src={`${ME_PAGE_LINK}/images/logo.svg`}
              alt="Logo"
            />
          </a>

          <Offcanvas
            show={isShow}
            onHide={() => isMenuShow(false)}
            placement="end"
          >
            <div className="offcanvas-header position-relative d-flex justify-content-center">
              <h5 className="offcanvas-title" id="offcanvasNavbarLabel">
                <img
                  width={91}
                  src={`${ME_PAGE_LINK}/images/logo.svg`}
                  alt="Logo"
                />
              </h5>
              <button
                type="button"
                className="btn-close position-absolute menu-close-button fs-5"
                onClick={() => isMenuShow(false)}
              ></button>
            </div>
            <div className="offcanvas-body">
              <div onClickCapture={handleRestrictedNavigation}>
                <ul className="nav left-nav-pills nav-block flex-column mb-sm-auto mb-0 align-items-center align-items-sm-start px-lg-4 w-100 fs-7">
                  <li className="nav-item w-100 mb-2">
                    <Dropdown>
                      <Dropdown.Toggle
                        variant="primary"
                        id="dropdown-basic"
                        className="text-white py-2 text-white fs-7 w-100 fw-normal d-flex justify-content-between align-items-center"
                      >
                        <div>
                          <PlusLg className="me-2" />
                          <span>{t("menu.create")}</span>
                        </div>
                        <ChevronDown />
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="w-100 fs-7">
                        {user?.user?.domains.length > 0 && (
                          <Dropdown.Item
                            as={Link}
                            to={{
                              pathname: `${lng}/page/create`,
                              search: `?type=1${id ? `&parentFolder=${id}` : ""}${projectId ? `&projectId=${projectId}` : ""}${!isNoShared ? `&Shared` : ""}`,
                            }}
                            className="py-2 px-3 mb-1"
                            onClick={() => {
                              window.gtag?.("event", "create_page");
                            }}
                          >
                            <FileEarmark className="me-2" />
                            {t("menu.page")}
                          </Dropdown.Item>
                        )}
                        {projectId &&
                        (!projectAccessType || projectAccessType === 2) &&
                        (!folderAccessType || folderAccessType === 2) ? (
                          <>
                            <Dropdown.Item
                              as={Link}
                              className=" py-2 px-3 mb-1"
                              onClick={() => {
                                window.gtag?.("event", "create_folder");
                                handleCloseOffcanvas();
                              }}
                              to={createFolderLink}
                            >
                              <Folder className="me-2" />
                              {t("menu.folder")}
                            </Dropdown.Item>
                          </>
                        ) : null}
                        <Dropdown.Item
                          className=" py-2 px-3 mb-1"
                          as={Link}
                          to={`${lng}/projects/new`}
                          onClick={() => {
                            window.gtag?.("event", "create_project");
                            handleCloseOffcanvas();
                          }}
                        >
                          <Star className="me-2" />
                          {t("menu.project")}
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </li>
                  {user?.user.admin && (
                    <>
                      <li className="nav-item w-100 mb-2">
                        <Link
                          to={`${lng}/page/all`}
                          className={`nav-link ${location.pathname.includes("/page/all") ? "active" : ""}`}
                          onClick={handleCloseOffcanvas}
                        >
                          <Plus className="me-2 fs-4" />
                          <span>All Pages</span>
                        </Link>
                      </li>
                      <li className="nav-item w-100 mb-2">
                        <Link
                          to={`${lng}/folder/all`}
                          className={`nav-link ${location.pathname.includes("/folder/all") ? "active" : ""}`}
                          onClick={handleCloseOffcanvas}
                        >
                          <Plus className="me-2 fs-4" />
                          <span>All Folders</span>
                        </Link>
                      </li>
                    </>
                  )}
                  <li className="nav-item w-100 mb-2">
                    <Link
                      to={`${lng}/projects`}
                      className={`nav-link ${location.pathname.includes("/projects") ? "active" : ""}`}
                      onClick={handleCloseOffcanvas}
                    >
                      <Star className="me-2" />
                      <span>{t("menu.myProjects")}</span>
                    </Link>
                  </li>
                  <Projects handleCloseOffcanvas={handleCloseOffcanvas} />
                </ul>

                <ul className="nav left-nav-pills nav-block flex-column mb-sm-auto mb-0 align-items-center align-items-sm-start px-lg-4 w-100 fs-7">
                  <li className="nav-item w-100 mb-2 ps-3">
                    <Link
                      to={`${lng}/shared`}
                      className={`nav-link ${location.pathname.includes("/shared") ? "active" : ""}`}
                      onClick={handleCloseOffcanvas}
                    >
                      <Share className="me-2" />
                      <span>{t("menu.shared")}</span>
                    </Link>
                  </li>
                  <li className="nav-item w-100 mb-2">
                    <Link
                      to={`${lng}/profile`}
                      className={`nav-link ${location.pathname.includes("/profile") ? "active" : ""}`}
                      onClick={handleCloseOffcanvas}
                    >
                      <Person className="me-2" />
                      <span>{t("menu.profile")}</span>
                    </Link>
                  </li>
                  <li className="nav-item w-100 mb-2">
                    <Link
                      to={`${lng}/statistic-dashboard`}
                      className={`nav-link ${location.pathname.includes("/statistic-dashboar") ? "active" : ""}`}
                      onClick={handleCloseOffcanvas}
                    >
                      <BarChart className="me-2" />
                      <span>{t("menu.statistics")}</span>
                    </Link>
                  </li>
                  <li className="nav-item w-100 mb-2">
                    <Link
                      to={`${lng}/payment`}
                      className={`nav-link ${location.pathname.includes("/payment") ? "active" : ""}`}
                      onClick={handleCloseOffcanvas}
                    >
                      <CreditCard2Back className="me-2" />
                      <span>{t("menu.paymentSubscription")}</span>
                    </Link>
                  </li>
                  <li className="nav-item w-100 mb-2">
                    <Link
                      to={`${lng}/support`}
                      className={`nav-link ${location.pathname.includes("/support") ? "active" : ""}`}
                      onClick={handleCloseOffcanvas}
                    >
                      <QuestionCircle className="me-2" />
                      <span>{t("menu.support")}</span>
                    </Link>
                  </li>
                  {user?.user.admin && (
                    <li className="nav-item w-100 mb-2">
                      <Link
                        to="/template"
                        className={`nav-link ${location.pathname.includes("/template") ? "active" : ""}`}
                        onClick={handleCloseOffcanvas}
                      >
                        <LayoutTextWindowReverse className="me-2" />
                        <span>{t("menu.templates")}</span>
                      </Link>
                    </li>
                  )}
                  {user?.user.admin && (
                    <li className="nav-item w-100 mb-2">
                      <Link
                        to="/page/category"
                        className={`nav-link ${location.pathname.includes("/page/category") ? "active" : ""}`}
                        onClick={handleCloseOffcanvas}
                      >
                        <QuestionCircle className="me-2" />
                        <span>Page Categories</span>
                      </Link>
                    </li>
                  )}
                  {user?.user.admin && (
                    <li className="nav-item w-100 mb-2">
                      <Link
                        to="/template/category"
                        className={`nav-link ${location.pathname.includes("/template/category") ? "active" : ""}`}
                        onClick={handleCloseOffcanvas}
                      >
                        <QuestionCircle className="me-2" />
                        <span>Template Categories</span>
                      </Link>
                    </li>
                  )}
                  <li className="nav-item w-100 mb-2">
                    <Link
                      to={`${lng}/subpage-templates`}
                      className={`nav-link ${location.pathname.includes("/subpage-templates") ? "active" : ""}`}
                      onClick={handleCloseOffcanvas}
                    >
                      <QuestionCircle className="me-2" />
                      <span>Subpage Templates</span>
                    </Link>
                  </li>
                  {user?.user.admin && (
                    <li className="nav-item w-100 mb-2">
                      <Link
                        to={`${process.env.REACT_APP_ME_PAGE_URL}/blog-admin/blog/article`}
                        className={`nav-link`}
                        onClick={handleCloseOffcanvas}
                        target="_blank"
                      >
                        <QuestionCircle className="me-2" />
                        <span>Blog Articles</span>
                      </Link>
                    </li>
                  )}
                  {user?.user.admin && (
                    <>
                      <li className="nav-item w-100 mb-2">
                        <Link
                          to={`${lng}/user-administration`}
                          className={`nav-link ${location.pathname.includes("/user-administration") ? "active" : ""}`}
                          onClick={handleCloseOffcanvas}
                        >
                          <QuestionCircle className="me-2" />
                          <span>Users</span>
                        </Link>
                      </li>
                      <li className="nav-item w-100 mb-2">
                        <Link
                          to={`${lng}/page-blocker`}
                          className={`nav-link ${location.pathname.includes("/page-blocker") ? "active" : ""}`}
                          onClick={handleCloseOffcanvas}
                        >
                          <QuestionCircle className="me-2" />
                          <span>Page Blocker</span>
                        </Link>
                      </li>
                    </>
                  )}
                </ul>
              </div>
              <div className="pt-3 pt-md-5 mt-md-5 px-4">
                <UserData />
                <Dropdown className="col-12 mb-3 mb-md-4 positon-static">
                  <Dropdown.Toggle
                    id="dropdown-basic"
                    variant="link"
                    x-placement="bottom-start"
                    className="d-flex align-items-center justify-content-between nav-link dropdown-toggle text-capitalize text-gray col-12"
                  >
                    <div className="d-flex">
                      <Globe className="me-3" />
                      <small>{t("general.Languages")}</small>
                    </div>
                    <ChevronDown className="ms-2" />
                  </Dropdown.Toggle>

                  <Dropdown.Menu
                    className="dropdown-menu-custom"
                    x-placement="bottom-start"
                  >
                    {languages.map((item) => (
                      <Dropdown.Item
                        className={`fs-7 ${lng.slice(1) === item.value && "text-primary"} ${item.value === "en" && lng === "" && "text-primary"}`}
                        key={item.value}
                        onClick={() => {
                          changeLanguage(item.value);
                          handleCloseOffcanvas();
                        }}
                      >
                        {item.label}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
                <div className="d-flex flex-column text-gray">
                  <div className="">
                    {user &&
                      (user?.user?.originalUserIfSwitched ? (
                        <div
                          className="nav-link cursor-pointer"
                          onClick={handleExitImpersonate}
                        >
                          <BoxArrowRight className="me-3" />
                          <span className="fs-7">Exit</span>{" "}
                          {loadingExit && <Spinner size="sm" />}
                        </div>
                      ) : (
                        <a href="#" className="nav-link" onClick={handleExit}>
                          <BoxArrowRight className="me-3" />
                          <span className=" fs-7">{t("menu.logout")}</span>
                        </a>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </Offcanvas>
        </div>
      </nav>
    </>
  );
}
